import Head from 'next/head';
import NextError from '@/pages/_error';
import { withSession } from '@zopauk/nextjs-auth';
import { Placeholder, Card } from '@/components/Cards';
import { LoadingSection } from '@/components/LoadingSection';
import { useQueryCards } from '@/utils/queries/useQueryCards';
import { MfaNotification } from '@/components/MfaNotification';
import { useQueryMember } from '@/utils/queries/useQueryMember';
import { MaintenanceBanner } from '@/components/MaintenanceBanner';
import { useQueryCreditCards } from '@/utils/queries/useQueryCreditCards';
import { InvestorsP2PClosureBanner } from '@/components/InvestorsP2PClosureBanner';
import { FlexContainer, Heading, colors, Alert, Align } from '@zopauk/react-components';

function HomePage() {
  const cardsQuery = useQueryCards();
  const memberQuery = useQueryMember();
  const creditCardsQuery = useQueryCreditCards();

  if (memberQuery?.data) {
    const member = memberQuery.data.data;
    const productCards = cardsQuery.data?.data.product.cards;
    const discoveryCards = cardsQuery.data?.data.discovery.cards;
    const productCardsErrors = cardsQuery.data?.data?.product?.errors || [];

    return (
      <>
        <Head>
          <title>Welcome to Zopa</title>
          <meta content="A dashboard to help manage your finances and feel good about money" name="description" />
        </Head>
        <div style={{ background: `linear-gradient(0deg, ${colors.brandLight}, transparent)` }} className="pt-5 pb-8">
          <FlexContainer style={{ maxWidth: '800px' }}>
            <Align xs="center">
              <Heading className="mb-8 mt-8" as="h1" data-amp-mask>
                Hi, {member.firstName} {member.lastName}
              </Heading>
            </Align>

            <MaintenanceBanner />

            <InvestorsP2PClosureBanner />

            <MfaNotification />

            <Heading color={colors.greyDarkest} className="mb-4" size="h4" as="h2">
              Your Products
            </Heading>

            {cardsQuery.error || productCardsErrors.length || creditCardsQuery.error ? (
              <Alert data-automation="ZA.technical-problem" severity="warning" hasRoundedCorners className="mb-6">
                Sorry, there is currently a technical problem which may result in some of your products not showing
                correctly.
              </Alert>
            ) : null}

            {productCards ? (
              productCards.map((card, index) => <Card {...card} key={`${card.template}_${index}`} />)
            ) : (
              <Placeholder type="primary" />
            )}

            <hr
              style={{
                background: `radial-gradient(${colors.grey}80, transparent 70%)`,
                marginTop: '30px',
                display: 'block',
                height: '1px',
                width: '100%',
                border: '0',
              }}
            />
          </FlexContainer>
        </div>

        <div style={{ background: `linear-gradient(180deg, ${colors.brandLight}, transparent)` }} id="what-else">
          <FlexContainer style={{ maxWidth: '800px' }}>
            <Heading color={colors.greyDarkest} className="mb-6" size="h4" as="h2">
              See what else we do
            </Heading>

            {discoveryCards ? (
              discoveryCards.map((card, index) => <Card {...card} key={`${card.template}_${index}`} />)
            ) : (
              <Placeholder type="discovery" />
            )}
          </FlexContainer>
        </div>
      </>
    );
  }

  if (memberQuery.error) {
    return <NextError statusCode={500} />;
  }

  return <LoadingSection />;
}

export default withSession(HomePage);
